import {
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Stack,
} from "@mui/material";
import { DbUser, LtiTools } from "../../../../interfaces";
import { UserManagementModal } from "../UserManagementModal";
import { makeStyles } from "@mui/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import React from "react";

const useStyles = makeStyles({
  textField: {
    "& .MuiFormLabel-root": {
      fontWeight: 500,
      fontSize: 14,
      color: "#747474",
      textTransform: "uppercase",
      letterSpacing: 0.7,
    },
    "& .MuiInputBase-root": {
      color: "#484848",
      borderBottom: "none !important",
      fontSize: 16,
      fontWeight: 400,
      paddingBottom: "0.5rem",
      "& .Mui-disabled": {
        "-webkit-text-fill-color": "unset",
      },
    },
  },
  borderBottom: {
    width: "100%",
    borderBottom: "1px solid #E8E8E8",
  },
});

export type ViewLtiToolProps = {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  currentUser: DbUser;
  tool: LtiTools | null;
};

export function ViewLtiTool(props: ViewLtiToolProps) {
  const classes = useStyles();
  const { tool } = props;

  const fieldProps = {
    fullWidth: true,
    disabled: true,
    className: classes.textField,
    InputProps: {
      disableUnderline: true,
    },
  };

  const divider = <div className={classes.borderBottom} />;

  const [showSecret, setShowSecret] = React.useState(false);

  const handleClickShowPassword = () => setShowSecret((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const getBackEndUrl = () => {
    return process.env.REACT_APP_BACKEND_API;
  };

  return (
    <UserManagementModal
      hideSubmitButton={true}
      title={tool?.toolName ?? tool?.clientId ?? ""}
      open={props.open}
      onClose={props.onClose}>
      <Stack direction={"column"} justifyContent={"flex-end"} mb={4}>
        <Typography variant={"h5"} mb={4}>
          Details to be shared with Tool Provider
        </Typography>
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <TextField
              label="Issuer"
              value={"https://digit.asu.edu"}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Authentication Endpoint"
              value={`${getBackEndUrl()}/lti/1p3/auth`}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Client ID"
              value={tool?.clientId}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Client Secret"
              value={tool?.clientSecret}
              variant="standard"
              {...fieldProps}
              type={showSecret ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end">
                      {showSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Deployment ID"
              value={tool?.deploymentId}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Access Token Endpoint"
              value={`${getBackEndUrl()}/lti/1p3/token?clientId=${tool?.clientId}`}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Platform Public Key"
              value={`${getBackEndUrl()}/lti/security/jwks`}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
        </Grid>
      </Stack>
      {divider}
      <Stack direction={"column"} justifyContent={"flex-end"} mb={4}>
        <Typography variant={"h5"}>Tool Configuration</Typography>
        <Grid container mt={4} spacing={6}>
          <Grid item xs={6}>
            <TextField
              label="Tool Name"
              value={tool?.toolName}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          {/*<Grid item xs={6}>*/}
          {/*  <TextField*/}
          {/*    label="DB Row ID"*/}
          {/*    value={tool?.id}*/}
          {/*    variant="standard"*/}
          {/*    {...fieldProps}*/}
          {/*  />*/}
          {/*  {divider}*/}
          {/*</Grid>*/}
          <Grid item xs={12}>
            <TextField
              label="Tool Description"
              value={tool?.toolDescription}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="OIDC Endpoint"
              value={tool?.toolOidcEndpoint}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Launch URL"
              value={tool?.toolLaunchUrl}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          {/* For array or optional fields, ensure to handle them appropriately */}
          <Grid item xs={6}>
            <TextField
              label="Redirect URIs"
              value={tool?.toolRedirectUris?.join(", \n") ?? ""}
              variant="standard"
              type={"multiline"}
              rows={3}
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="JWK Method"
              value={tool?.toolJwkMethod}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Public JWK URL"
              value={tool?.toolPublicJwkUrl ?? ""}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Created At"
              value={
                tool?.createdAt
                  ? new Date(tool.createdAt).toLocaleDateString("en-us", {
                      timeZone: "UTC",
                    })
                  : ""
              }
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          {/*<Grid item xs={6}>*/}
          {/*  <TextField*/}
          {/*    label="Updated At"*/}
          {/*    value={tool?.updatedAt ? new Date(tool.updatedAt).toLocaleDateString("en-us", {*/}
          {/*      timeZone: "UTC"*/}
          {/*    }) : ""}*/}
          {/*    variant="standard"*/}
          {/*    {...fieldProps}*/}
          {/*  />*/}
          {/*  {divider}*/}
          {/*</Grid>*/}
          <Grid item xs={12}>
            <TextField
              label="Metadata"
              value={JSON.stringify(tool?.toolMetadata)}
              variant="standard"
              multiline
              {...fieldProps}
            />
            {divider}
          </Grid>
        </Grid>
      </Stack>
    </UserManagementModal>
  );
}
